import React from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Divider,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  Menu as MenuIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { 
  Home, 
  Search, 
  Person, 
  Logout, 
  Dashboard, 
  People, 
  Settings, 
  Group,
  Analytics,
  Business,
  Lock,
  WhatsApp,
  Delete
} from '@mui/icons-material';
import { useAuth } from '../hooks/useAuth';

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg')); // Show mobile menu on screens smaller than lg (1200px)
  
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuToggle = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
    handleClose();
    handleMobileMenuClose();
  };

  const handleProfile = () => {
    navigate('/profile');
    handleClose();
    handleMobileMenuClose();
  };

  const handlePasswordReset = () => {
    navigate('/password-reset');
    handleClose();
    handleMobileMenuClose();
  };

  // Don't show navigation on login page
  if (location.pathname === '/login') {
    return null;
  }

  // Navigation items for authenticated users
  const authenticatedNavItems = [
    { label: 'Dashboard', path: '/dashboard', icon: <Dashboard /> },
    { label: 'Clientes', path: '/clients', icon: <Group /> },
    { label: 'Parceiros', path: '/partners', icon: <Business /> },
    { label: 'Financeiro', path: '/financial-dashboard', icon: <Analytics /> },
    { label: 'Configurações', path: '/settings', icon: <Settings /> },
    ...(user?.role === 'admin' ? [{ label: 'Usuários', path: '/users', icon: <People /> }] : []),
    ...(user?.role === 'admin' ? [{ label: 'Clientes Deletados', path: '/deleted-clients', icon: <Delete /> }] : []),
    ...(user?.role === 'admin' ? [{ label: 'WhatsApp', path: '/whatsapp', icon: <WhatsApp /> }] : [])
  ];

  // Navigation items for public users
  const publicNavItems = [
    { label: 'Início', path: '/register', icon: <Home /> },
    { label: 'Acompanhar', path: '/tracking', icon: <Search /> },
    { label: 'Parceiros', path: '/partner-tracking', icon: <Business /> }
  ];

  const navItems = user ? authenticatedNavItems : publicNavItems;

  const renderDesktopNavigation = () => (
    <>
      {/* Navigation Links - Center */}
      {user && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
          {authenticatedNavItems.map((item) => (
            <Button
              key={item.path}
              component={RouterLink}
              to={item.path}
              startIcon={item.icon}
              sx={{
                color: 'inherit',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      )}

      {/* Public Navigation Links */}
      {!user && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
          {publicNavItems.map((item) => (
            <Button
              key={item.path}
              component={RouterLink}
              to={item.path}
              startIcon={item.icon}
              sx={{
                color: 'inherit',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              {item.label}
            </Button>
          ))}
        </Box>
      )}
    </>
  );

  const renderMobileDrawer = () => (
    <Drawer
      anchor="left"
      open={mobileMenuOpen}
      onClose={handleMobileMenuClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: 280,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
          Activoo
        </Typography>
        <IconButton onClick={handleMobileMenuClose}>
          <CloseIcon />
        </IconButton>
      </Box>
      <Divider />
      
      <List>
        {navItems.map((item) => (
          <ListItem key={item.path} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={item.path}
              onClick={handleMobileMenuClose}
              sx={{
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              }}
            >
              <ListItemIcon>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.label} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {user && (
        <>
          <Divider />
          <List>
            <ListItem disablePadding>
              <ListItemButton onClick={handleProfile}>
                <ListItemIcon>
                  <Person />
                </ListItemIcon>
                <ListItemText primary="Perfil" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handlePasswordReset}>
                <ListItemIcon>
                  <Lock />
                </ListItemIcon>
                <ListItemText primary="Alterar Senha" />
              </ListItemButton>
            </ListItem>
            <ListItem disablePadding>
              <ListItemButton onClick={handleLogout}>
                <ListItemIcon>
                  <Logout />
                </ListItemIcon>
                <ListItemText primary="Sair" />
              </ListItemButton>
            </ListItem>
          </List>
        </>
      )}
    </Drawer>
  );

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          {/* Mobile Menu Button */}
          {isMobile && (
            <IconButton
              edge="start"
              color="inherit"
              aria-label="menu"
              onClick={handleMobileMenuToggle}
              sx={{ mr: 2 }}
            >
              <MenuIcon />
            </IconButton>
          )}

          {/* Activoo Logo - Left Side */}
          <Typography
            variant="h6"
            component={RouterLink}
            to="/"
            sx={{
              flexGrow: isMobile ? 1 : 0,
              textDecoration: 'none',
              color: 'inherit',
              fontWeight: 800,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              mr: isMobile ? 0 : 4,
            }}
          >
            Activoo
          </Typography>

          {/* Desktop Navigation */}
          {!isMobile && (
            <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
              {renderDesktopNavigation()}
            </Box>
          )}

          {/* User Menu - Right Side */}
          {user ? (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IconButton
                size="large"
                aria-label="account of current user"
                aria-controls="menu-appbar"
                aria-haspopup="true"
                onClick={handleMenu}
                sx={{ color: 'inherit' }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                  {user.username.charAt(0).toUpperCase()}
                </Avatar>
              </IconButton>
              <Menu
                id="menu-appbar"
                anchorEl={anchorEl}
                anchorOrigin={{
                  vertical: 'bottom',
                  horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                open={Boolean(anchorEl)}
                onClose={handleClose}
              >
                <MenuItem onClick={handleProfile} sx={{ minWidth: 200 }}>
                  <Person sx={{ mr: 2 }} />
                  Perfil
                </MenuItem>
                <MenuItem onClick={handlePasswordReset}>
                  <Lock sx={{ mr: 2 }} />
                  Alterar Senha
                </MenuItem>
                <MenuItem onClick={handleLogout}>
                  <Logout sx={{ mr: 2 }} />
                  Sair
                </MenuItem>
              </Menu>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                component={RouterLink}
                to="/login"
                variant="contained"
                size={isMobile ? "small" : "medium"}
                sx={{
                  fontWeight: 600,
                  boxShadow: 'none',
                  '&:hover': {
                    boxShadow: 'none',
                  },
                }}
              >
                Entrar
              </Button>
            </Box>
          )}
        </Toolbar>
      </AppBar>

      {/* Mobile Drawer */}
      {isMobile && renderMobileDrawer()}
    </>
  );
} 